import { useCallback, useMemo, useRef, useState } from "react";
import TableContainer from "components/common/TableContainer";
import { Link } from "react-router-dom";
import classnames from "classnames";
import { useViewInvoicePdf } from "features/Invoices/hooks/useViewInvoicePdf";

import { ColumnDef } from "@tanstack/react-table";
import StatusBadge from "components/common/StatusBadge";
import {
  CodeCell,
  IndexColumn,
  SourceColumn,
  resolveMappedSourceCell,
} from "components/common/columns";
import { DateOnly, DateTime } from "components/common/Date";
import {
  Card,
  CardBody,
  CardHeader,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Nav,
  NavItem,
  NavLink,
  Spinner,
  UncontrolledDropdown,
} from "reactstrap";
import { Invoice } from "features/Invoices/types/invoice.types";
import {
  InvoiceListSourceTypeParamLabelMap,
  InvoiceSourceTypeLabelMap,
  InvoiceStatus,
  InvoiceStatusMap,
} from "features/Invoices/constants/invoice.constants";
import { cancelInvoice, getInvoices } from "features/Invoices/services/InvoiceService";
import { formatCurrency } from "utils/currency";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import CancelInvoiceModal from "components/common/CancelInvoiceModal";
import InvoiceListFilters from "./InvoiceListFilters";

/** Row `sourceType` may match API enums or list-filter tokens (e.g. `AMC_CONTRACT`). */
const INVOICE_SOURCE_TYPE_LABELS: Record<string, string> = {
  ...(InvoiceSourceTypeLabelMap as Record<string, string>),
  ...(InvoiceListSourceTypeParamLabelMap as Record<string, string>),
};

type InvoiceTab = "all" | "unpaid" | "overdue";

const UNPAID_STATUSES = `${InvoiceStatus.ISSUED},${InvoiceStatus.PARTIALLY_PAID}`;

export const InvoicesTable = () => {
  const { can } = useAccess();
  const canReadInvoice = can(Permissions.INVOICE.READ);
  const canUpdateInvoice = can(Permissions.INVOICE.UPDATE);
  const canCancelInvoice = can(Permissions.INVOICE.CANCEL);
  const tableRef = useRef<() => void>(null);
  const { viewPdf, loadingId: pdfLoadingId } = useViewInvoicePdf();

  const [activeTab, setActiveTab] = useState<InvoiceTab>("all");
  const [statusFilter, setStatusFilter] = useState("");
  const [sourceTypeFilter, setSourceTypeFilter] = useState("");
  const [clientIdFilter, setClientIdFilter] = useState<number | null>(null);
  const [cancelTarget, setCancelTarget] = useState<Invoice | null>(null);

  const handleTabChange = useCallback((tab: InvoiceTab) => {
    setActiveTab(tab);
    setStatusFilter("");
  }, []);

  const requestParams = useMemo(() => {
    const today = new Date().toISOString().split("T")[0];
    return {
      status: activeTab === "all" ? statusFilter || undefined : UNPAID_STATUSES,
      sourceType: sourceTypeFilter || undefined,
      clientId: clientIdFilter ?? undefined,
      dueDateTo: activeTab === "overdue" ? today : undefined,
    };
  }, [activeTab, clientIdFilter, sourceTypeFilter, statusFilter]);

  const filtersBadgeCount = useMemo(() => {
    let n = 0;
    if (statusFilter) n += 1;
    if (sourceTypeFilter) n += 1;
    if (clientIdFilter != null) n += 1;
    return n;
  }, [clientIdFilter, sourceTypeFilter, statusFilter]);

  const clearListFilters = useCallback(() => {
    setStatusFilter("");
    setSourceTypeFilter("");
    setClientIdFilter(null);
  }, []);

  const columns: ColumnDef<Invoice>[] = useMemo(
    () => [
      IndexColumn<Invoice>(),
      {
        header: "Code",
        cell: ({ row }) => {
          const r = row.original;
          return canReadInvoice ? (
            <Link
              to={`/finance/invoices/${r.id}`}
              className="text-decoration-none link"
            >
              <CodeCell code={r.code} />
            </Link>
          ) : (
            <CodeCell code={r.code} />
          );
        },
        accessorKey: "code",
        enableColumnFilter: false,
      },
      SourceColumn<Invoice>({
        resolve: (row) =>
          resolveMappedSourceCell(row.sourceType, row.sourceId, {
            typeLabelMap: INVOICE_SOURCE_TYPE_LABELS,
            source: row.source,
          }),
        accessorFn: (row) =>
          [row.sourceType, row.source?.code, row.sourceId]
            .filter((v) => v != null && v !== "")
            .join(" "),
      }),

      {
        header: "Total amount",
        accessorKey: "totalAmount",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const total = Number(row.original.totalAmount ?? 0);
          return <span className="text-body">{formatCurrency(total)}</span>;
        },
      },
      {
        header: "Amount due",
        cell: ({ row }) => {
          const { outstandingAmount, dueDate } = row.original;
          const due = Number(outstandingAmount ?? 0);
          const isPaid = due <= 0;
          const isOverdue =
            !isPaid && dueDate && new Date(dueDate) < new Date();

          return (
            <div>
              <span
                className={`fw-semibold ${isOverdue ? "text-danger" : "text-body"}`}
              >
                {formatCurrency(due)}
              </span>
              {isOverdue ? (
                <div className="text-danger" style={{ fontSize: "0.72rem" }}>
                  Overdue
                </div>
              ) : null}
            </div>
          );
        },
        enableColumnFilter: false,
      },

      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        cell: ({ row }) => {
          return (
            <StatusBadge value={row.original.status} map={InvoiceStatusMap} />
          );
        },
      },
      {
        header: "Due date",
        accessorKey: "dueDate",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const d = row.original.dueDate;
          return d ? (
            <DateOnly date={d} />
          ) : (
            <span className="text-muted">—</span>
          );
        },
      },
      {
        header: "Created",
        accessorKey: "created_at",
        enableColumnFilter: false,
        cell: ({ row }) => {
          return <DateTime date={row.original.created_at} />;
        },
      },

      {
        header: "Actions",
        id: "actions",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          const r = row.original;
          const showCancel =
            canCancelInvoice && r.status !== InvoiceStatus.CANCELLED;
          const hasMenu =
            canReadInvoice ||
            (canUpdateInvoice && r.status !== InvoiceStatus.CANCELLED) ||
            showCancel;

          if (!hasMenu) {
            return <span className="text-muted small">—</span>;
          }

          return (
            <UncontrolledDropdown>
              <DropdownToggle
                type="button"
                className="btn btn-soft-secondary btn-sm"
                tag="button"
                aria-label="Invoice actions"
              >
                <i className="ri-more-2-fill align-middle" />
              </DropdownToggle>
              <DropdownMenu container="body" end>
                {canReadInvoice ? (
                  <DropdownItem tag={Link} to={`/finance/invoices/${r.id}`}>
                    <i className="ri-eye-line align-bottom me-2 text-muted" />
                    View
                  </DropdownItem>
                ) : null}
                {canUpdateInvoice && r.status !== InvoiceStatus.CANCELLED ? (
                  <DropdownItem
                    tag={Link}
                    to={`/finance/invoices/edit/${r.id}`}
                  >
                    <i className="ri-pencil-line align-bottom me-2 text-muted" />
                    Edit
                  </DropdownItem>
                ) : null}
                <DropdownItem
                  tag="button"
                  type="button"
                  onClick={() => viewPdf(r.id)}
                  disabled={pdfLoadingId != null}
                >
                  {pdfLoadingId === r.id ? (
                    <Spinner size="sm" className="me-2" />
                  ) : (
                    <i className="ri-file-pdf-2-line align-bottom me-2 text-muted" />
                  )}
                  Export PDF
                </DropdownItem>
                {showCancel ? (
                  <>
                    <DropdownItem divider />
                    <DropdownItem
                      className="text-danger"
                      onClick={() => setCancelTarget(r)}
                    >
                      <i className="ri-close-circle-line align-bottom me-2" />
                      Cancel invoice
                    </DropdownItem>
                  </>
                ) : null}
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      },
    ],
    [canReadInvoice, canUpdateInvoice, canCancelInvoice, viewPdf, pdfLoadingId],
  );

  return (
    <>
    <Card>
      <CardHeader className="border-0 pb-0">
        <Nav
          className="nav-tabs-custom card-header-tabs border-bottom-0"
          role="tablist"
        >
          <NavItem>
            <NavLink
              className={classnames(
                { active: activeTab === "all" },
                "fw-semibold cursor-pointer",
              )}
              onClick={() => handleTabChange("all")}
            >
              All
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={classnames(
                { active: activeTab === "unpaid" },
                "fw-semibold cursor-pointer",
              )}
              onClick={() => handleTabChange("unpaid")}
            >
              Unpaid
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={classnames(
                { active: activeTab === "overdue" },
                "fw-semibold cursor-pointer",
              )}
              onClick={() => handleTabChange("overdue")}
            >
              Overdue
            </NavLink>
          </NavItem>
        </Nav>
      </CardHeader>
      <CardBody>
        <TableContainer
          columns={columns}
          fetchApi={getInvoices}
          requestParams={requestParams}
          pageSize={10}
          globalSearch={true}
          SearchPlaceholder="Search by code, client, source…"
          refreshRef={tableRef}
          divClass="table-responsive table-card mb-3"
          tableClass="align-middle table-nowrap mb-0"
          theadClass="table-light text-muted"
          toolbarRight={
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <InvoiceListFilters
                section="inline"
                status={statusFilter}
                onStatusChange={setStatusFilter}
                sourceType={sourceTypeFilter}
                onSourceTypeChange={setSourceTypeFilter}
                clientId={clientIdFilter}
                onClientIdChange={setClientIdFilter}
                issueDateFrom=""
                onIssueDateFromChange={() => {}}
                issueDateTo=""
                onIssueDateToChange={() => {}}
                dueDateFrom=""
                onDueDateFromChange={() => {}}
                dueDateTo=""
                onDueDateToChange={() => {}}
              />
              {filtersBadgeCount > 0 && (
                <button
                  type="button"
                  className="btn btn-soft-danger d-flex align-items-center gap-1"
                  onClick={clearListFilters}
                >
                  <i className="ri-close-circle-line" />
                  Clear
                </button>
              )}
            </div>
          }
        />
      </CardBody>
    </Card>
    <CancelInvoiceModal
      show={cancelTarget != null}
      onClose={() => setCancelTarget(null)}
      onSuccess={() => {
        setCancelTarget(null);
        tableRef.current?.();
      }}
      submit={(payload) => {
        if (!cancelTarget) throw new Error("No invoice selected.");
        return cancelInvoice(cancelTarget.id, payload);
      }}
    />
    </>
  );
};
